import { useState } from 'react';

import { motion } from 'framer-motion';
import {
  ArrowRight,
  Cloud,
  Database,
  GitBranch,
  Settings,
  Shield,
  Users,
  Zap,
} from 'lucide-react';

const integrations = [
  { name: 'CrowdStrike', icon: Shield, category: 'Security', id: 'crowdstrike' },
  { name: 'Okta', icon: Users, category: 'Identity', id: 'okta' },
  { name: 'ServiceNow', icon: Settings, category: 'CMDB', id: 'servicenow' },
  { name: 'AWS', icon: Cloud, category: 'Cloud', id: 'aws' },
  { name: 'Splunk', icon: Database, category: 'SIEM', id: 'splunk' },
  { name: 'OpenAI', icon: Zap, category: 'AI', id: 'openai' },
  { name: 'Velociraptor', icon: Shield, category: 'DFIR', id: 'velociraptor' },
  { name: 'Jira', icon: GitBranch, category: 'Ticketing', id: 'jira' },
];

// BOSSS blue color as specified
const BOSSS_BLUE = '#2973a3';

export function IntegrationsSection({ onNavigateToTop }) {
  const [hoveredIntegration, setHoveredIntegration] = useState(null);

  // Calculate positions for integrations in a perfect circle
  const centerX = 300; // Center X coordinate
  const centerY = 300; // Center Y coordinate
  const radius = 200; // Distance from center to integration cards

  const getIntegrationPosition = (index) => {
    const angle = (index / integrations.length) * 2 * Math.PI - Math.PI / 2; // Start from top
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    return { x, y, angle };
  };

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-16"
          transition={{ duration: 0.8 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4 mr-2" />
            Seamless Integrations
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            95+ Seamless Integrations
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            BOSSS connects with your existing infrastructure across 18 categories—from SIEM platforms and cloud providers to identity systems, vulnerability scanners, AI providers, and threat intelligence feeds—providing unified security
            oversight.
          </p>
        </motion.div>

        {/* Integration Diagram */}
        <div className="relative flex justify-center items-center mb-20">
          <div className="relative w-[600px] h-[600px]">
            {/* SVG for connection lines */}
            <svg
              viewBox="0 0 600 600"
              className="absolute inset-0 w-full h-full pointer-events-none"
            >
              {integrations.map((integration, index) => {
                const pos = getIntegrationPosition(index);
                const isHovered = hoveredIntegration === integration.id;

                return (
                  <motion.line
                    x2={pos.x}
                    y2={pos.y}
                    x1={centerX}
                    y1={centerY}
                    stroke={BOSSS_BLUE}
                    key={integration.id}
                    strokeWidth={isHovered ? '3' : '2'}
                    initial={{ pathLength: 0, opacity: 0.6 }}
                    animate={{
                      pathLength: 1,
                      opacity: isHovered ? 1 : 0.6,
                      strokeWidth: isHovered ? 3 : 2,
                    }}
                    transition={{
                      duration: 0.8,
                      delay: 0.5 + index * 0.1,
                      opacity: { duration: 0.2 },
                      strokeWidth: { duration: 0.2 },
                    }}
                  />
                );
              })}
            </svg>

            {/* Central BOSSS Node */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20"
            >
              <div
                className="w-32 h-32 rounded-full flex items-center justify-center shadow-2xl border-4"
                style={{
                  background: `linear-gradient(135deg, ${BOSSS_BLUE}, #1e5f8b)`,
                  borderColor: BOSSS_BLUE,
                }}
              >
                <div className="text-center">
                  <Shield className="w-12 h-12 text-white mx-auto mb-2" />
                  <span className="text-white font-bold text-lg">BOSSS</span>
                </div>
              </div>
            </motion.div>

            {/* Integration Cards */}
            {integrations.map((integration, index) => {
              const IconComponent = integration.icon;
              const pos = getIntegrationPosition(index);
              const isHovered = hoveredIntegration === integration.id;

              return (
                <motion.div
                  key={integration.id}
                  viewport={{ once: true }}
                  className="absolute z-10"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  style={{
                    left: pos.x - 60, // Half of card width (120px)
                    top: pos.y - 60, // Half of card height (120px)
                  }}
                  onMouseLeave={() => setHoveredIntegration(null)}
                  onMouseEnter={() => setHoveredIntegration(integration.id)}
                >
                  <motion.div
                    transition={{ duration: 0.2 }}
                    className="w-[120px] h-[120px] bg-white rounded-xl shadow-lg border-2 hover:shadow-xl transition-all duration-300 cursor-pointer"
                    whileHover={{
                      scale: 1.1,
                      y: -5,
                    }}
                    style={{
                      borderColor: isHovered ? BOSSS_BLUE : '#e5e7eb',
                    }}
                  >
                    <div className="flex flex-col items-center justify-center h-full p-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 transition-all duration-300"
                        style={{
                          backgroundColor: isHovered ? `${BOSSS_BLUE}15` : '#f3f4f6',
                        }}
                      >
                        <IconComponent
                          className="w-6 h-6 transition-colors duration-300"
                          style={{ color: isHovered ? BOSSS_BLUE : '#6b7280' }}
                        />
                      </div>

                      <h4 className="font-semibold text-gray-900 text-center text-sm leading-tight mb-1">
                        {integration.name}
                      </h4>

                      <span
                        className="text-xs px-2 py-1 rounded-full transition-colors duration-300"
                        style={{
                          backgroundColor: isHovered ? `${BOSSS_BLUE}15` : '#f3f4f6',
                          color: isHovered ? BOSSS_BLUE : '#6b7280',
                        }}
                      >
                        {integration.category}
                      </span>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Integration Categories Breakdown */}
        <motion.div
          className="mb-16"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">18 Integration Categories</h3>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { category: 'AI Providers', count: 12, icon: Zap },
                { category: 'Security & EDR', count: 30, icon: Shield },
                { category: 'SIEM & SOC', count: 8, icon: Database },
                { category: 'Identity & Access', count: 4, icon: Users },
                { category: 'Vulnerability Mgmt', count: 5, icon: Shield },
                { category: 'Cloud Platforms', count: 4, icon: Cloud },
                { category: 'Ticketing & ITSM', count: 5, icon: Settings },
                { category: 'Communication', count: 5, icon: Database },
                { category: 'HR Systems', count: 3, icon: Users },
                { category: 'GRC Platforms', count: 3, icon: Shield },
                { category: 'CRM', count: 2, icon: Users },
                { category: 'CMDB', count: 3, icon: Settings },
                { category: 'Developer Tools', count: 2, icon: GitBranch },
                { category: 'SSO Providers', count: 2, icon: Users },
                { category: 'Data Enrichment', count: 3, icon: Database },
                { category: 'Storage', count: 2, icon: Cloud },
                { category: 'Observability', count: 1, icon: Settings },
                { category: 'ERP', count: 1, icon: Settings },
              ].map((cat, i) => {
                const IconComponent = cat.icon;
                return (
                  <motion.div
                    key={i}
                    viewport={{ once: true }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: 0.9 + i * 0.05 }}
                    className="p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-8 h-8 rounded-lg bg-gradient-to-br flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${BOSSS_BLUE}20, ${BOSSS_BLUE}10)` }}
                      >
                        <IconComponent className="w-4 h-4" style={{ color: BOSSS_BLUE }} />
                      </div>

                      <span style={{ color: BOSSS_BLUE }} className="text-2xl font-bold">{cat.count}</span>
                    </div>

                    <p className="text-sm font-medium text-gray-900">{cat.category}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Total:
                {' '}
                <span className="font-bold text-gray-900">95+ native integrations</span>
                {' '}
                covering all aspects of security operations
              </p>
            </div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          <div className="text-center">
            <div
              style={{ backgroundColor: `${BOSSS_BLUE}15` }}
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              >
                <Settings className="w-8 h-8" style={{ color: BOSSS_BLUE }} />
              </motion.div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Automated Provisioning</h3>

            <p className="text-gray-600">
              Automatically sync user data and provision access across all connected systems.
            </p>
          </div>

          <div className="text-center">
            <div
              style={{ backgroundColor: `${BOSSS_BLUE}15` }}
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Database className="w-8 h-8" style={{ color: BOSSS_BLUE }} />
              </motion.div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Real-time Data Sync</h3>

            <p className="text-gray-600">
              Keep all your systems in sync with real-time data updates and change notifications.
            </p>
          </div>

          <div className="text-center">
            <div
              style={{ backgroundColor: `${BOSSS_BLUE}15` }}
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
            >
              <motion.div
                animate={{ y: [-2, 2, -2] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Zap className="w-8 h-8" style={{ color: BOSSS_BLUE }} />
              </motion.div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Pre-built Connectors</h3>

            <p className="text-gray-600">
              Get started quickly with our library of pre-built connectors and APIs.
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-200 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Don&apos;t see your integration?
            </h3>

            <p className="text-gray-600 mb-8">
              Our team can build custom integrations to connect BOSSS with any system in your environment.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileTap={{ scale: 0.95 }}
                style={{ backgroundColor: BOSSS_BLUE }}
                whileHover={{ scale: 1.05, boxShadow: `0 10px 25px ${BOSSS_BLUE}40` }}
                className="inline-flex items-center text-white font-medium px-6 py-3 rounded-lg transition-all duration-200 hover:scale-105 shadow-lg"
                onClick={() => onNavigateToTop('/services/integration')}
              >
                Custom Integration Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center border-2 hover:bg-blue-50 font-medium px-6 py-3 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  borderColor: BOSSS_BLUE,
                  color: BOSSS_BLUE,
                }}
                onClick={() => onNavigateToTop('contact')}
              >
                Contact Our Team
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
